"use client";

import { CosmosTitle } from "@cosmos/web/react";
import { TeaserBasic } from "../../TeaserBasic/TeaserBasic";
import classnames from "classnames/bind";
import styles from "./BenefitsList.module.css";
// import gsap from "gsap";
// import ScrollTrigger from "gsap/ScrollTrigger";
import { Prose } from "../../prose/prose";
import { useImageLoading } from "../../../helpers/ImageLoadingContext";
import { responsiveImageHelper } from "../../../helpers/responsiveImageHelper";
import { getImageSource } from "../../../helpers/getImageSource";
import { getStoryblokVideoUrl } from "../../../helpers/images/getStoryblokVideoUrl";

const cx = classnames.bind(styles);

// gsap.registerPlugin(ScrollTrigger);

type BenefitItem = {
  title: string;
  text: React.ReactNode;
};

export function BenefitsList({
  kicker,
  title,
  text,
  benefits,
  imageUrl,
  imageAlt,
  videoUrl,
}: {
  kicker: string;
  title: string;
  text?: React.ReactNode;
  benefits?: BenefitItem[];
  imageUrl?: string;
  imageUrlSrcSet?: string;
  imageAlt?: string;
  videoUrl?: string;
}) {
  const imageLoading = useImageLoading();

  const imageSource = imageUrl ? getImageSource(imageUrl) : null;

  return (
    <div className={cx("container")}>
      <div className={cx("inner")}>
        <TeaserBasic
          kicker={kicker}
          title={title}
          titleSize="x-large"
          headingElement="title"
          appearance="dark"
        />

        <div className={cx("content")}>
          {(imageUrl || videoUrl) && (
            <div className={cx("media-wrap")}>
              {/* only show one type of content, video takes precedence */}
              {imageUrl && !videoUrl && (
                <>
                  {imageSource === "contentful" ? (
                    <img
                      loading={imageLoading}
                      className={cx("img")}
                      srcSet={responsiveImageHelper.createContentfulSrcSet(
                        imageUrl,
                      )}
                      sizes="(min-width: 1000px) 500px, 400px"
                      alt={imageAlt}
                    />
                  ) : imageSource === "storyblok" ? (
                    <img
                      loading={imageLoading}
                      className={cx("img")}
                      srcSet={responsiveImageHelper.createStoryblokSrcSet(
                        imageUrl,
                      )}
                      sizes="(min-width: 1000px) 500px, 400px"
                      alt={imageAlt}
                    />
                  ) : (
                    <img
                      src={imageUrl}
                      alt={imageAlt}
                      loading={imageLoading}
                      className={cx("img")}
                    />
                  )}
                </>
              )}
              {videoUrl && (
                <video
                  className={cx("video")}
                  preload="auto"
                  autoPlay={true}
                  playsInline={true}
                  loop={true}
                  muted={true}
                  src={getStoryblokVideoUrl(videoUrl)}
                />
              )}
            </div>
          )}

          {benefits && benefits.length !== 0 && (
            <div className={cx("benefits")}>
              {text && (
                <div className={cx("point")}>
                  <Prose>{text}</Prose>
                </div>
              )}

              {benefits?.map((item, index) => (
                <div key={index} className={cx("point")}>
                  {item.title && (
                    <CosmosTitle size="small">{item.title}</CosmosTitle>
                  )}
                  {item.text && <Prose>{item.text}</Prose>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
